import type { FilteringCriteria, FilteringSourceCriteria } from "../../modules/criteria/criteriaTypes";
import { Option } from "@familyzone/component-library";

type MultiOption = Option & {
  color?: string;
};

export interface BaseCriteria {
  conditions: string[] | { [key: string]: string | string[] };
  type: string;
}

export interface BaseOption extends MultiOption {
  label: string;
  conditions: BaseCriteria["conditions"];
  type: BaseCriteria["type"];
  value: string;
}

export interface SignatureCriteria extends BaseCriteria {
  conditions: string[];
  type: "signature";
}

export interface SignatureOption extends MultiOption {
  label: string;
  value: string;
  type: "signature";
  conditions: SignatureCriteria["conditions"];
}

export interface KeywordCriteria extends BaseCriteria {
  conditions: { signature: string };
  type: "search.keywords";
}

export interface KeywordOption extends MultiOption {
  label: string;
  value: string;
  type: "search.keywords";
  conditions: KeywordCriteria["conditions"];
}

export interface KeywordObjectCriteria extends BaseCriteria {
  conditions: { alias: string };
  type: "search.keywords";
}

export interface KeywordObjectOption extends MultiOption {
  label: string;
  value: string;
  type: "search.keywords";
  conditions: KeywordObjectCriteria["conditions"];
}

export interface WebsiteCriteria extends BaseCriteria {
  conditions: { source_entries: string[] };
  type: "application.http.hostname";
}

export interface WebsiteOption extends MultiOption {
  label: string;
  value: string;
  type: "application.http.hostname";
  conditions: WebsiteCriteria["conditions"];
}

export interface WebsiteObjectCriteria extends BaseCriteria {
  conditions: string[];
  type: "application.http.hostname";
}

export interface WebsiteObjectOption extends MultiOption {
  label: string;
  value: string;
  type: "application.http.hostname";
  conditions: WebsiteObjectCriteria["conditions"];
}

export const YoutubeListCriteriaType = "youtube" as const;

export interface YoutubeListObjectCriteria extends BaseCriteria {
  conditions: string[];
  type: typeof YoutubeListCriteriaType;
}

export interface YoutubeListObjectOption extends MultiOption {
  label: string;
  value: string;
  type: typeof YoutubeListCriteriaType;
  conditions: YoutubeListObjectCriteria["conditions"];
}

export interface AllHTTPCriteria extends BaseCriteria {
  conditions: { [key: string]: string | string[] } | string[];
  type: "application.http";
}

export interface AllHTTPOption extends MultiOption {
  label: string;
  value: "application.http";
  type: "application.http";
  conditions: AllHTTPCriteria["conditions"];
}

export interface HTTPDirectIpCriteria extends BaseCriteria {
  conditions: { [key: string]: string | string[] } | string[];
  type: "application.http.directip";
}

export interface HTTPDirectIpOption extends MultiOption {
  label: string;
  value: "application.http.directip";
  type: "application.http.directip";
  conditions: HTTPDirectIpCriteria["conditions"];
}

export interface AllTrafficCriteria extends BaseCriteria {
  conditions: { [key: string]: string | string[] } | string[];
  type: "application";
}

export interface AllTrafficOption extends MultiOption {
  label: string;
  value: "application";
  type: "application";
  conditions: AllTrafficCriteria["conditions"];
}

export interface UnclassifiedTrafficCriteria extends BaseCriteria {
  conditions: { [key: string]: string | string[] } | string[];
  type: "application.http.unclassified";
}

export interface UnclassifiedTrafficOption extends MultiOption {
  label: string;
  value: "application.http.unclassified";
  type: "application.http.unclassified";
  conditions: UnclassifiedTrafficCriteria["conditions"];
}

export type AllCriteria =
  | SignatureCriteria
  | KeywordCriteria
  | KeywordObjectCriteria
  | WebsiteObjectCriteria
  | AllHTTPCriteria
  | HTTPDirectIpCriteria
  | AllTrafficCriteria
  | UnclassifiedTrafficCriteria
  | WebsiteCriteria;

export type AllOptions =
  | SignatureOption
  | KeywordOption
  | KeywordObjectOption
  | WebsiteObjectOption
  | AllHTTPOption
  | HTTPDirectIpOption
  | AllTrafficOption
  | UnclassifiedTrafficOption
  | WebsiteOption
  | YoutubeListObjectOption;

export interface FilteringRuleHeader {
  name: string;
  value: string;
}

export interface FilteringRule {
  content_mod?: string[];
  action: 0 | 1;
  criteria: BaseCriteria[];
  custom_headers?: {
    upsert: FilteringRuleHeader[];
  };
  enable_micro_lock: boolean;
  enabled: boolean;
  exclusive: boolean;
  expiry_timestamp: number;
  fireEvent: boolean;
  id: string;
  name: string;
  redirect: boolean;
  redirectUrl: string;
  redirect_to_standard: boolean;
  source_criteria: FilteringSourceCriteria[];
  sticky: boolean;
  temp_rule: boolean;
  top: boolean;
}

export function emptyFilteringCriteria(): FilteringCriteria {
  return {
    conditions: [],
    type: "",
  };
}

export function emptyFilteringSourceCriteria(): FilteringSourceCriteria {
  return {
    conditions: [],
    id: "",
    name: "",
    type: "",
  };
}

export function emptyFilteringRule(): FilteringRule {
  return {
    action: 0,
    criteria: [emptyFilteringCriteria()],
    enable_micro_lock: false,
    enabled: false,
    exclusive: false,
    expiry_timestamp: 0,
    fireEvent: false,
    id: "",
    name: "",
    redirect: false,
    redirectUrl: "",
    redirect_to_standard: false,
    source_criteria: [emptyFilteringSourceCriteria()],
    sticky: false,
    temp_rule: false,
    top: false,
  };
}

export interface SourceCriteriaError {
  id: string;
  msg: string;
}

export interface TimePeriod {
  any: boolean;
  endDate: number;
  endTime: number;
  fri: boolean;
  id: string;
  mon: boolean;
  name: string;
  sat: boolean;
  startDate: number;
  startTime: number;
  sun: boolean;
  thu: boolean;
  tue: boolean;
  wed: boolean;
}

export interface Group {
  archived?: boolean;
  azureObjectId?: string;
  desc: string;
  dn: string;
  domain: string;
  name: string;
  owners?: string[];
  provider_type: string;
  sub_groups?: string[];
}

export interface User {
  groups: string[];
  provider: string;
  username: string;
  archived?: boolean;
  fname?: string;
  lname?: string;
  password?: string;
  quota_meet?: number;
  resolved_groups?: Group[];
}

export interface Directive {
  id: string;
  name: string;
  description: string;
  conditions: { type: string; value: string }[];
  actions: { type: string; value: string; action: string; target: string }[];
  favicon_url: string;
}

export interface Verdict {
  verdict: number;
  method: string;
  rule?: Omit<FilteringRule, "criteria"> & {
    criteria: [{ conditions: FilteringCriteria[]; type: "or" }];
  };
  zoom?: Zoom;
  signatures?: { signature: string; category: string; subCategory: string };
  redirect_uri?: string;
  debug?: VerdictDebug;
}

export interface VerdictDebug {
  community_student_config?: CommunityStudentConfig;
}

export interface CommunityStudentConfig {
  pauseStart: string;
  pauseEnd: string;
}

export function emptyVerdict(): Verdict {
  return {
    verdict: 1,
    method: "",
  };
}

export function validateVerdict(v: unknown): v is Verdict {
  const validatedVerdict = v as Verdict;

  return validatedVerdict?.verdict !== undefined && validatedVerdict?.method !== undefined;
}

export interface ZoomPolicyLE {
  id: string;
  website: string;
  permanent: boolean;
}

export function emptyZoomPolicyLE(): ZoomPolicyLE {
  return {
    id: "",
    website: "",
    permanent: false,
  };
}

export interface ZoomPolicy {
  reward: string[];
  allowed: ZoomPolicyLE[];
  blocked: ZoomPolicyLE[];
  exceptions: string[];
  chat_blocked: boolean;
  lockedUsers: string[];
  internet_blocked: boolean;
  bypass_active_until: number;
  reward_active_until: number;
  internet_blocked_lifetime: number;
  internet_blocked_override: number;
}

export function emptyZoomPolicy(): ZoomPolicy {
  return {
    reward: [],
    allowed: [emptyZoomPolicyLE()],
    blocked: [emptyZoomPolicyLE()],
    exceptions: [],
    chat_blocked: false,
    lockedUsers: [],
    internet_blocked: false,
    bypass_active_until: 0,
    reward_active_until: 0,
    internet_blocked_lifetime: 0,
    internet_blocked_override: 0,
  };
}

export interface Zoom {
  group: string;
  sourceName: string;
  policy: ZoomPolicy;
  reward: string[];
  schedule: {
    periods: string[];
    start_time: number;
    active_until: number;
  };
  xirsysChannel: string;
  reward_active_until: number;
  xirsysChannelTimeout: number;
}

export function emptyZoom(): Zoom {
  return {
    group: "",
    sourceName: "",
    policy: emptyZoomPolicy(),
    reward: [],
    schedule: {
      periods: [],
      start_time: 0,
      active_until: 0,
    },
    xirsysChannel: "",
    reward_active_until: 0,
    xirsysChannelTimeout: 0,
  };
}

export interface SafeSearchRule {
  bing_enabled: boolean;
  enabled: boolean;
  id: string;
  source_criteria: FilteringSourceCriteria[];
  youtube_enabled: boolean;
  youtube_moderate_enabled: boolean;
}

export interface Signature {
  id: string;
  name: string;
  category: string;
  enabled: boolean;
  is_category: boolean;
  is_sub_category: boolean;
  is_low_level_signature?: boolean;
  is_high_level_signature?: boolean;
  non_exclusive_criteria: { [key: string]: unknown }[];
  noise: boolean;
  url: string;
  favicon_url: string;
  focus: boolean;
  reporting: boolean;
  hidden: boolean;
  tags: string[];
  app_ids: { [key: string]: string };
  content_hub: { [key: string]: string };
  description: string;
  dependencies: string[];
}

export const lockedOptions: Option[] = [
  {
    label: "Locked",
    value: "locked",
  },
  {
    label: "Unlocked",
    value: "unlocked",
  },
];

export interface Keyword {
  deprecated: boolean;
  id: string;
  name: string;
  regions?: string[];
}

export type BypassCode = {
  code: string;
  created_by: string;
  creation_time: number;
  expiry_time: number;
  id: string;
};
